// ----------------------------------------------------------------------
// <copyright file="Izenda.ConditionalFormattingSetting.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * ConditionalFormattingSetting Component
 */
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import constants from 'logic/data/Izenda.ConditionalFormattingConstants';
import { Table, Column } from 'share/components/controls/DataGrid/Izenda.Table';
import { constant, filter, get, getOr, isString, isNumber } from 'lodash/fp';
import ConditionalFormattingRule from './Izenda.ConditionalFormattingRule';
import ConditionalFormattingConditionPopup from './Izenda.ConditionalFormattingConditionPopup';
import ConditionalFormattingFieldsPopup from './Izenda.ConditionalFormattingFieldsPopup';
import ConditionalFormattingFormatPopup from './Izenda.ConditionalFormattingFormatPopup';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import Button from 'share/components/controls/Izenda.Button';
import conditionalFormattingUtils from './Izenda.ConditionalFormattingUtils';

const {CONDITION_MANAGER_TABLE_DEF: TABLE_DEF, CONDITION_TYPE, FORMAT_TYPE } = constants;

@observer
class ConditionalFormattingSetting extends Component{
  @observable rules;

  gridProperties = {};
  fields = [];

  constructor(props) {
    super(props);
  }

  componentWillMount(){
    const {props: {value: {reportPartContent, conditionalFormatting: {rules} }}} = this;
    this.gridProperties = conditionalFormattingUtils.getGridProperties(reportPartContent);
    this.fields = conditionalFormattingUtils.groupReportPartElements(reportPartContent);
    this.rules = rules.map(rule => new ConditionalFormattingRule(rule));
  }

  // Return data for update data.
  getValue() {
    return {
      rules: this.rules.filter(rule => !rule.isEmpty()).map(rule => rule.serializeData())
    };
  }

  onAddRuleBtnClick = () => {
    const rule = new ConditionalFormattingRule();
    this.rules.push(rule);
  }

  onRuleMove = (oldPosititon, newPosititon, selectedRule) => {
    const {rules} = this;
    rules.splice(oldPosititon, 1);
    rules.splice(newPosititon, 0, selectedRule);
  }

  getCellContentLink = (text, onClickHandler) => {
    return (<a onClick={onClickHandler}><span style={{'marginRight': '4px', 'lineHeight': '22px'}} className="u_textUnderline">{text}</span></a>);
  }

  getConditionValue = (condition) => {
    let conditionValueString = '';
    let isPercentSymbolRequierd = false;
    const conditionType = get('type', condition);
    switch(conditionType){
      case CONDITION_TYPE.ALL_VALUES.value:
        conditionValueString = CONDITION_TYPE.ALL_VALUES.title;
        break;
      case CONDITION_TYPE.VALUE.value:
        conditionValueString = get('value', condition);
        break;
      case CONDITION_TYPE.RANGE_PERCENT.value:
        isPercentSymbolRequierd = true;
      case CONDITION_TYPE.RANGE_VALUE.value:
        let from = get('value.from', condition);
        let to = get('value.to', condition);

        if(isPercentSymbolRequierd){
          from = isNumber(from) ? from + '%' : from;
          to = isNumber(to) ? to + '%' : to;
        }

        if(from && to)
          conditionValueString = from + ' - ' + to;

        break;
    }
    return conditionValueString;
  }

  getAdditionalConditionsString = (additionalConditions, applyToSideTotal) => {
    const { getConditionValue } = this;

    let additionalConditionsString = '';

    if(applyToSideTotal){
      additionalConditionsString += ' + Side Total';
    }

    if(additionalConditions.length > 0){
      let isFirstAdditionalCondition = true;
      additionalConditions.forEach(additionalCondition => {
        if(isFirstAdditionalCondition){
          additionalConditionsString += ' WHERE ';
          isFirstAdditionalCondition = false;
        }else{
          additionalConditionsString += ' AND ';
        }

        const additionalConditionFieldName = get('conditionField.name', additionalCondition);
        const additionalConditionValue = getConditionValue(additionalCondition);
        additionalConditionsString += additionalConditionFieldName + ' = ' + additionalConditionValue;
      });
    }

    return additionalConditionsString;
  }

  renderConditionCell = (rule) => {
    const {getCellContentLink, getConditionValue, getAdditionalConditionsString, fields, gridProperties, props: {openPopup}} = this;

    const onConditionCellClick = () => {
      const onConditionChange = (newCondition) => {
        rule.condition = newCondition;
      };

      openPopup(ConditionalFormattingConditionPopup, {
        gridProperties: gridProperties,
        fields: fields,
        condition: rule.condition,
        onConditionChange: onConditionChange
      }); 
    };

    const condition = get('condition', rule);
    const additionalConditions = getOr([], 'additionalConditions', condition);
    let conditionFieldName = get('conditionField.name', condition) || constants.NOT_SELECTED_COLUMN_VALUE;
    conditionFieldName += getAdditionalConditionsString(additionalConditions);

    const renderConditionValue = () => {
      const conditionValue = getConditionValue(condition);
      if(!conditionValue)
        return null;
      return (<div className="conditional-formatting-value-wrapper"><span title={conditionValue} className="overflow-text">{conditionValue}</span></div>);
    }

    return (
      <div>
        {getCellContentLink(conditionFieldName, onConditionCellClick)}
        {renderConditionValue()}
      </div>
    );
  }

  renderFieldsCell = (rule) => {
    const {getCellContentLink, getAdditionalConditionsString, fields, gridProperties, props: {openPopup}} = this;

    const onFieldsCellClick = () => {
      const onFieldsChange = (selectedFields) => {
        rule.fields = selectedFields;
      };

      openPopup(ConditionalFormattingFieldsPopup, {
        gridProperties: gridProperties,
        fields: fields,
        selectedFields: rule.fields,
        onFieldsChange: onFieldsChange
      });
    };

    let selectedFieldsValue = constants.NOT_SELECTED_COLUMN_VALUE;
    if(rule.fields.length !== 0)
      selectedFieldsValue = rule.fields.map(field => {
        if(isString(field)){
          return field;
        }else{
          const additionalConditions = getOr([], 'additionalConditions', field);
          return field.name + getAdditionalConditionsString(additionalConditions, field.applyToSideTotal);
        }
      }).join(', ');

    return getCellContentLink(selectedFieldsValue, onFieldsCellClick);
  }

  renderFormatCell = (rule) => {
    const {getCellContentLink, props: {openPopup}} = this;

    const onFormatCellClick = () => {
      const onFormatChange = (newFormat) => {
        rule.format = newFormat;
      };

      openPopup(ConditionalFormattingFormatPopup, {
        format: rule.format,
        onFormatChange: onFormatChange
      });
    };

    const formatTypeTitle = get('format.type.title', rule) || constants.NOT_SELECTED_COLUMN_VALUE;
    const formatTypeValue = get('format.type.value', rule);

    let isColorFormatValue = false;
    let formatValueString = '';
    switch(formatTypeValue){
      case FORMAT_TYPE.FONT_COLOR.value:
      case FORMAT_TYPE.BACKGROUND_COLOR.value:
        isColorFormatValue = true;
      case FORMAT_TYPE.ALTERNATIVE_TEXT.value:
        formatValueString = get('format.value', rule);
        break;
      case FORMAT_TYPE.FONT_FORMAT.value:
        const fontFamily = get('format.value.font.fontFamily', rule);
        const fontSize = get('format.value.font.fontSize', rule);
        formatValueString = fontFamily + ' ' + fontSize;

        const fontBold = get('format.value.style.fontBold', rule);
        if(fontBold)
          formatValueString += ' Bold';

        const fontItalic = get('format.value.style.fontItalic', rule);
        if(fontItalic)
          formatValueString += ' Italic';

        const fontUnderline = get('format.value.style.fontUnderline', rule);
        if(fontUnderline)
          formatValueString += ' Underline';

        break;
    }

    const renderFormatValue = () => {
      if(!formatValueString)
        return null;

      if(isColorFormatValue)
        return (<span className="conditional-formatting-color-value" style={{'backgroundColor': formatValueString}}></span>);

      if(!isColorFormatValue)
        return (<div className="conditional-formatting-value-wrapper"><span title={formatValueString} className="overflow-text">{formatValueString}</span></div>);
    }

    return (
      <div>
        {getCellContentLink(formatTypeTitle, onFormatCellClick)}
        {renderFormatValue()}
      </div>
    );
  }

  renderActionCell = (rule) => {
    const {localization} = this.props;
    const deleteRule = () => {
      this.rules = filter(ruleItem => ruleItem.id != rule.id, this.rules);
    };
    const cloneRule = () => {
      const ruleData = JSON.stringify(rule); 
      const newRule = new ConditionalFormattingRule(JSON.parse(ruleData));
      this.rules.push(newRule);
    };
    return (
      <div>
        <a onClick={cloneRule}><span title={localization.getText('CONDITIONAL_FORMATTING_LABEL_CLONE_RULE')} className="icon-copy" /></a>
        <span> </span>
        <a onClick={deleteRule}><span title={localization.getText('CONDITIONAL_FORMATTING_LABEL_DELETE_RULE')} className="icon-cancel" /></a>
      </div>
    );
  }

  render(){
    const {
      props: {localization}, onAddRuleBtnClick, rules,
      renderConditionCell, renderFieldsCell, renderFormatCell,
      renderActionCell, onRuleMove
    } = this;

    return (
      <div className="izenda-ConditionalFormattingSetting">
        <GridLayout className="form-horizontal">
          <GridLayout.Row className="form-group">
            <GridLayout.Col className="u_textRight">
              <Button onClick={onAddRuleBtnClick} className="btn-report-default" label={localization.getText('CONDITIONAL_FORMATTING_LABEL_ADD_RULE')} />
            </GridLayout.Col>
          </GridLayout.Row>
          <GridLayout.Row className="form-group">
            <GridLayout.Col>
              <Table
                dataSource={rules}
                isLoading={true}
                draggableTable={true}
                canDrag={constant(true)}
                canDrop={constant(true)}
                move={onRuleMove}>
                  <Column {...TABLE_DEF['condition']} cell={renderConditionCell} />
                  <Column {...TABLE_DEF['fields']} cell={renderFieldsCell} />
                  <Column {...TABLE_DEF['format']} cell={renderFormatCell} />
                  <Column {...TABLE_DEF['action']} cell={renderActionCell} />
              </Table>
            </GridLayout.Col>
          </GridLayout.Row>
        </GridLayout>
      </div>
    );
  }
}

export default ConditionalFormattingSetting;



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/ConditionalFormatting/Izenda.ConditionalFormattingSetting.jsx